import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import OrderCard from "@/components/whatsapp-monitoring/order-card";
import { buildMetadata } from "@/lib/metadata";
import { WhapiService } from "@/services/whapi-service";
import { createFileRoute } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_main/whatsapp-monitoring/")({
  component: RouteComponent,
  head: () => ({
    meta: buildMetadata({
      title: "WhatsApp Monitoring",
      description: "WhatsApp Monitoring",
      keywords: ["whatsapp", "monitoring"],
    }),
  }),
  loader: async () => {
    // First fetch all whatsapp groups
    const groups = await WhapiService.getGroupsWithOrders();

    return { groups };
  },
});

function RouteComponent() {
  const { groups } = Route.useLoaderData();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all-statuses");
  const [chatFilter, setChatFilter] = useState("all-chats");

  // Helper function to determine the status of a group
  const getGroupStatus = (group: (typeof groups)[number]) => {
    // Check if any message in the group has order data with a status
    for (const msg of group.messages) {
      if (msg.orderData?.status) {
        return msg.orderData.status;
      }
    }

    // If no status found, determine if it's a new conversation
    // Check for any message with order data
    const hasOrderData = group.messages.some((msg) => msg.orderData !== null);

    // If there's no order data but there are messages, it could be a new conversation
    if (!hasOrderData && group.messages.length > 0) {
      return "New";
    }

    // If there's recent activity (message within last 24 hours), mark as new
    const latestMessage = group.messages[0];
    if (latestMessage?.created_at) {
      const messageDate = new Date(latestMessage.created_at);
      const now = new Date();
      const hoursDiff =
        (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60);

      if (hoursDiff < 24) {
        return "New";
      }
    }

    return "Pending"; // Default
  };

  // // Helper function to determine the chat status (New, Active, Closed)
  // const getChatStatus = (group: (typeof groups)[number]) => {
  //   // If there's a message within 24 hours, it's considered "New"
  //   const latestMessage = group.messages[0];
  //   if (latestMessage?.created_at) {
  //     const messageDate = new Date(latestMessage.created_at);
  //     const now = new Date();
  //     const hoursDiff =
  //       (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60);

  //     if (hoursDiff < 24) {
  //       return "new";
  //     }

  //     if (hoursDiff < 72) {
  //       return "active";
  //     }

  //     return "closed";
  //   }

  //   return "active"; // Default
  // };

  // Filter and search the groups
  const filteredGroups = groups.filter((group) => {
    // Text search filter
    const matchesSearch = searchQuery
      ? group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        group.messages.some((msg) =>
          msg.content?.some((content) =>
            content.text?.body
              ?.toLowerCase()
              .includes(searchQuery.toLowerCase()),
          ),
        )
      : true;

    // Status filter
    const groupStatus = getGroupStatus(group);
    const matchesStatus =
      statusFilter === "all-statuses"
        ? true
        : (statusFilter === "pending" && groupStatus === "Pending") ||
          (statusFilter === "approved" && groupStatus === "Approved") ||
          (statusFilter === "rejected" && groupStatus === "Rejected") ||
          (statusFilter === "new" && groupStatus === "New");

    // // Chat filter
    // const chatStatus = getChatStatus(group);
    // const matchesChat =
    //   chatFilter === "all-chats" ? true : chatFilter === chatStatus;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="font-semibold text-xl">
          Here's an overview of your orders
        </h2>
      </div>

      {/* Header */}
      <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div className="flex items-center gap-2">
          <h1 className="font-medium text-2xl">WhatsApp Communications</h1>
          <Badge className="bg-brand-yellowGreen text-brand-darkGreen">
            {filteredGroups.reduce((total, group) => {
              // Count unique orders by checking orderData.id
              const uniqueOrderIds = new Set();

              for (const msg of group.messages) {
                if (msg.orderData && typeof msg.order === "number") {
                  uniqueOrderIds.add(msg.orderData.id);
                }
              }

              return total + uniqueOrderIds.size;
            }, 0)}{" "}
            orders
          </Badge>
        </div>

        {/* Controls Section: Adjusted classes for better wrapping and stacking */}
        <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center">
          {/* Search Input */}
          <div className="relative w-full md:w-auto">
            <Input
              placeholder="Search Conversations"
              className="w-full pl-10 md:w-[300px]" // Keeps medium+ width
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
              <Search className="h-4 w-4 text-gray-500" />
            </div>
          </div>

          {/* Filter Selects: Adjusted classes for stacking and width */}
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Select
              defaultValue="all-statuses"
              value={statusFilter}
              onValueChange={setStatusFilter}
            >
              {/* Added w-full for small screens, sm:w-[140px] for larger */}
              <SelectTrigger className="w-full font-normal text-sm sm:w-[140px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-statuses">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="new">New</SelectItem>
              </SelectContent>
            </Select>

            {/* <Select
              defaultValue="all-chats"
              value={chatFilter}
              onValueChange={setChatFilter}
            >
              <SelectTrigger className="w-full font-normal text-sm sm:w-[140px]">
                <SelectValue placeholder="All Chats" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-chats">All Chats</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select> */}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {filteredGroups.length === 0 ? (
          <div className="col-span-full py-8 text-center text-gray-500">
            No matching order groups found
          </div>
        ) : (
          filteredGroups.map((group) => (
            <OrderCard key={group.id} group={group} />
          ))
        )}
      </div>
    </div>
  );
}
